import React from "react";
import { Box, Container, Heading, Text, VStack, HStack, Icon, Badge, Step, StepDescription, StepIcon, StepIndicator, StepNumber, StepSeparator, StepStatus, StepTitle, Stepper, useSteps, Grid, GridItem, SimpleGrid } from "@chakra-ui/react";
import { FaHandshake, FaClock, FaCheckCircle, FaFileAlt, FaDollarSign, FaRocket } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const SAND = "#C2A878";
const SAND_LIGHT = "#F5F0E8";

const approvalSteps = [
  { title: 'Application', description: 'Submit online in 5 minutes' },
  { title: 'Assessment', description: 'Automated credit check' },
  { title: 'Approval', description: 'Decision in 60 seconds' },
  { title: 'Funds Released', description: 'Same-day settlement' },
];

const FinanceServicesCustomerFinance: React.FC = () => {
  const { activeStep } = useSteps({
    index: 2,
    count: approvalSteps.length,
  });

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance/services/customer-finance"
        fallbackTitle="Customer Finance Solutions - One Group"
        fallbackDescription="Point-of-sale financing solutions for businesses. Increase sales with flexible payment options for your customers."
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Badge bg={SAND} color={NAVY} fontSize="md" px={4} py={2} borderRadius="full">
              B2B Finance Solutions
            </Badge>
            <Heading size="2xl" fontWeight="bold">
              Customer Finance Solutions
            </Heading>
            <Text fontSize="lg" maxW="3xl" opacity={0.9}>
              Offer your customers flexible payment options at point of sale. Increase conversion rates, average transaction values, and customer loyalty with seamless finance integration.
            </Text>
            <HStack spacing={8} mt={4}>
              <Box textAlign="center">
                <Text fontSize="3xl" fontWeight="bold">60 sec</Text>
                <Text fontSize="sm">Approval Time</Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="3xl" fontWeight="bold">35%</Text>
                <Text fontSize="sm">Sales Increase</Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="3xl" fontWeight="bold">$100K</Text>
                <Text fontSize="sm">Finance Limits</Text>
              </Box>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Approval Process Horizontal Stepper */}
      <Container maxW="container.xl" py={16}>
        <Heading textAlign="center" mb={12} size="xl">
          Fast Approval Process
        </Heading>
        <Stepper index={activeStep} colorScheme="gray" size="lg">
          {approvalSteps.map((step, index) => (
            <Step key={index}>
              <StepIndicator>
                <StepStatus
                  complete={<StepIcon />}
                  incomplete={<StepNumber />}
                  active={<StepNumber />}
                />
              </StepIndicator>

              <Box flexShrink="0">
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </Box>

              <StepSeparator />
            </Step>
          ))}
        </Stepper>

        <Box mt={12} p={8} bg="white" borderRadius="xl" shadow="lg" textAlign="center">
          <Heading size="md" mb={4}>From Application to Funds in Under 2 Hours</Heading>
          <Text color="gray.600">
            Our streamlined approval process means your customers get instant decisions and same-day settlement. No waiting, no complexity - just simple, fast finance.
          </Text>
        </Box>
      </Container>

      {/* Benefits Grid - Bento Box Style */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <Heading textAlign="center" mb={12} size="xl">
            Why Offer Customer Finance?
          </Heading>
          <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={6}>
            <GridItem bg={NAVY} color="white" p={10} borderRadius="2xl" colSpan={{ base: 1, lg: 2 }} rowSpan={2}>
              <Icon as={FaRocket} boxSize={16} mb={6} />
              <Heading size="lg" mb={4}>Increase Sales Conversion</Heading>
              <Text fontSize="lg" mb={6}>
                Convert more browsers into buyers. Customers are 65% more likely to complete a purchase when finance options are available.
              </Text>
              <VStack align="start" spacing={3}>
                <HStack>
                  <Icon as={FaCheckCircle} />
                  <Text>Higher conversion rates</Text>
                </HStack>
                <HStack>
                  <Icon as={FaCheckCircle} />
                  <Text>Larger transaction values</Text>
                </HStack>
                <HStack>
                  <Icon as={FaCheckCircle} />
                  <Text>Competitive advantage</Text>
                </HStack>
              </VStack>
            </GridItem>

            <Box bg="white" p={8} borderRadius="xl" border="2px solid" borderColor="gray.200">
              <Icon as={FaClock} boxSize={12} color={SAND} mb={4} />
              <Heading size="md" mb={3}>Instant Decisions</Heading>
              <Text color="gray.700">60-second automated approvals keep customers engaged at point of sale.</Text>
            </Box>

            <Box bg="white" p={8} borderRadius="xl" border="2px solid" borderColor="gray.200">
              <Icon as={FaDollarSign} boxSize={12} color={SAND} mb={4} />
              <Heading size="md" mb={3}>Get Paid Immediately</Heading>
              <Text color="gray.700">Receive full payment upfront while your customer pays over time.</Text>
            </Box>

            <GridItem bg={SAND_LIGHT} p={8} borderRadius="xl" border="2px solid" borderColor={SAND} colSpan={{ base: 1, lg: 2 }}>
              <Icon as={FaFileAlt} boxSize={12} color={NAVY} mb={4} />
              <Heading size="md" mb={3}>Simple Integration</Heading>
              <Text color="gray.700">
                Easy integration with your POS system, online checkout, or manual quote process. White-label options available to maintain your brand experience.
              </Text>
            </GridItem>

            <Box bg="white" p={8} borderRadius="xl" border="2px solid" borderColor="gray.200">
              <Icon as={FaHandshake} boxSize={12} color={SAND} mb={4} />
              <Heading size="md" mb={3}>No Risk to You</Heading>
              <Text color="gray.700">We handle all credit risk and collections. You focus on sales.</Text>
            </Box>
          </Grid>
        </Container>
      </Box>

      {/* Industries Served */}
      <Container maxW="container.xl" py={16}>
        <Heading textAlign="center" mb={12} size="xl">
          Industries We Serve
        </Heading>
        <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={6}>
          {[
            { name: "Solar & Energy", icon: "☀️" },
            { name: "Home Improvement", icon: "🏠" },
            { name: "Medical & Dental", icon: "🏥" },
            { name: "Electronics", icon: "💻" },
            { name: "Furniture", icon: "🛋️" },
            { name: "Automotive", icon: "🚗" }
          ].map((industry, idx) => (
            <VStack key={idx} p={6} bg="white" borderRadius="xl" shadow="md" spacing={3} border="1px solid" borderColor="gray.100">
              <Text fontSize="4xl">{industry.icon}</Text>
              <Text fontWeight="bold" textAlign="center" fontSize="sm">{industry.name}</Text>
            </VStack>
          ))}
        </SimpleGrid>
      </Container>

      {/* CTA */}
      <Box bg={NAVY} color="white" py={16}>
        <Container maxW="container.xl" textAlign="center">
          <Heading size="xl" mb={4}>Start Offering Finance to Your Customers</Heading>
          <Text fontSize="lg" mb={8}>Increase sales by 35% with point-of-sale financing</Text>
          <HStack justify="center" spacing={4}>
            <Box as="button" px={8} py={3} bg={SAND} color={NAVY} borderRadius="lg" fontWeight="bold">
              Become a Partner
            </Box>
            <Box as="button" px={8} py={3} bg="transparent" border="2px solid" borderColor="white" borderRadius="lg" fontWeight="bold">
              View Demo
            </Box>
          </HStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceServicesCustomerFinance;
